<template>
  <v-container>
    <v-card>
      <v-card-title>
        Envíos
        <v-spacer />
        <v-btn class="primary" :disabled="saving" @click="newDelivery">Nuevo Envío</v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>

        <v-row class="mb-2" align="center">
          <v-col cols="12" md="7" lg="6">
            <v-autocomplete
              v-model="selectedCompanyIds"
              :items="companyOptions"
              item-text="name"
              item-value="_id"
              label="Filtrar por empresa"
              placeholder="Todas las empresas"
              prepend-inner-icon="business"
              no-data-text="No hay empresas que coincidan"
              :disabled="loading"
              :menu-props="{ offsetY: true, maxHeight: 320 }"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              outlined
              dense
              hide-details
              class="company-filter"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < MAX_VISIBLE_CHIPS"
                  small
                  close
                  color="primary"
                  text-color="white"
                  @click:close="unselectCompany(item._id)"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === MAX_VISIBLE_CHIPS" class="grey--text text-caption ml-1">
                  y {{ selectedCompanyIds.length - MAX_VISIBLE_CHIPS }} más
                </span>
              </template>
              <template v-slot:item="{ item, attrs }">
                <v-list-item-action class="mr-3">
                  <!-- Icono y no v-simple-checkbox: ese corta la propagacion
                       del click y la fila no se marcaria al tocar la casilla. -->
                  <v-icon :color="attrs.inputValue ? 'primary' : ''">
                    {{ attrs.inputValue ? "check_box" : "check_box_outline_blank" }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small label>{{ item.count }}</v-chip>
                </v-list-item-action>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="5" lg="6" class="text-md-right grey--text text-body-2">
            <template v-if="selectedCompanyIds.length">
              Mostrando {{ filteredDeliveries.length }} de {{ deliveries.length }} Envíos
            </template>
            <template v-else>
              {{ deliveries.length }} Envíos
            </template>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="filteredDeliveries"
          :loading="loading"
          no-data-text="No hay Envíos cargados"
          loading-text=""
          :footer-props="{ itemsPerPageOptions: [15, 50, 100], itemsPerPageText: 'Mostrar' }"
        >
          <template v-slot:item.company="{ item }">
            {{ companyName(item) }}
            <v-tooltip v-if="hasDuplicateRisk(item)" top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="warning" small class="ml-1" v-bind="attrs" v-on="on">warning</v-icon>
              </template>
              <span>{{ duplicateRiskText(item) }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.purpose="{ item }">
            {{ purposeText(item.purpose) }}
          </template>
          <template v-slot:item.template="{ item }">
            {{ templateName(item) }}
          </template>
          <template v-slot:item.isActive="{ item }">
            {{ item.isActive ? "Sí" : "No" }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon :disabled="saving" @click="editDelivery(item)">
              <v-icon color="primary">mode_edit</v-icon>
            </v-btn>
            <v-btn icon :disabled="saving" @click="remove(item)">
              <v-icon color="error">delete</v-icon>
            </v-btn>
            <!-- De texto y no de icono: mandar un correo de prueba no es una
                 accion que convenga adivinar por un dibujo. El icono de avion
                 se confunde con "enviar el Envio ahora", que es justo lo que
                 NO hace: manda una prueba a una direccion que se tipea. -->
            <v-btn text small color="primary" :disabled="saving" @click="testDelivery(item)">
              Probar
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog :value="showDialog" max-width="900" persistent @input="cancelEdit">
      <!-- v-if, no solo el :value del dialogo: v-dialog usa el mixin bootable,
           que una vez abierto deja el contenido montado para siempre
           (hasContent = isBooted || eager || isActive). Sin esto el formulario
           sobrevive al cierre con deliveryForEdit en null -mismo bug que ya
           paso en la pantalla de empresas-. -->
      <edit-email-delivery
        v-if="deliveryForEdit"
        :delivery="deliveryForEdit"
        :purpose-options="purposes"
        @cancel="cancelEdit"
        @saved="onSaved"
      />
    </v-dialog>

    <!-- Sin v-dialog propio alrededor: delivery-test-dialog.component.vue ya
         trae el suyo, igual que tag-mailing-dialog.component.vue en
         edit-company.view.vue. El v-if es el mismo criterio de siempre
         (bootable de Vuetify): sin el, el modal quedaria montado para
         siempre despues de la primera apertura. -->
    <delivery-test-dialog
      v-if="deliveryForTest"
      :delivery-id="deliveryForTest._id"
      :attaches-sheet="attachesSheet(deliveryForTest)"
      :summary="testSummary(deliveryForTest)"
      @close="deliveryForTest = null"
    />
  </v-container>
</template>

<script>
"use strict";

import emailDeliveryService from "@/services/EmailDeliveryService";
import emailTemplateService from "@/services/EmailTemplateService";
import editEmailDelivery from "@/components/views/emails/edit-email-delivery.view.vue";
import deliveryTestDialog from "@/components/views/emails/delivery-test-dialog.component.vue";
import plainText from "@/filters/plain-text";

// Los tres propositos que el camino viejo tambien manda: diario y mensual con
// su propio interruptor en la empresa, y etiquetas con sus propios envios en
// company.tagMailings (que el sistema sigue procesando hoy). Son los unicos
// donde puede haber duplicado: el resto (recordatorio, fin de mes) no tiene
// equivalente viejo.
const PURPOSE_DAILY = "daily";
const PURPOSE_MONTHLY = "monthly";
const PURPOSE_TAG = "tag";

export default {
  name: "list-email-deliveries",
  components: { editEmailDelivery, deliveryTestDialog },
  data() {
    return {
      deliveries: [],
      purposes: [],
      loading: false,
      // Bandera "en vuelo": sin esto, un borrado que falla deja la fila en la
      // tabla como si hubiera funcionado y no bloquea el resto de los botones.
      saving: false,
      errorMessage: null,
      showDialog: false,
      deliveryForEdit: null,
      deliveryForTest: null,
      // Ids de empresa elegidos en el filtro. Vacio = sin filtro (todas).
      selectedCompanyIds: [],
      // Mas chips que esto empujan el campo a varias lineas; el resto se
      // resume en "y N mas".
      MAX_VISIBLE_CHIPS: 3,
      headers: [
        { text: "Empresa", value: "company" },
        { text: "Propósito", value: "purpose" },
        { text: "Plantilla", value: "template" },
        { text: "Activo", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false, width: 160 }
      ]
    };
  },
  computed: {
    // Las opciones salen de los Envios ya cargados y no de un pedido aparte de
    // empresas: asi solo se ofrecen empresas que tienen algo para mostrar, y
    // el contador de cada una sale gratis.
    companyOptions() {
      const byId = {};
      this.deliveries.forEach(delivery => {
        const company = delivery.company;
        if (!company || !company._id) { return; }
        if (!byId[company._id]) {
          byId[company._id] = { _id: company._id, name: company.name || "", count: 0 };
        }
        byId[company._id].count++;
      });
      return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name, "es"));
    },
    filteredDeliveries() {
      if (!this.selectedCompanyIds.length) { return this.deliveries; }
      const selected = new Set(this.selectedCompanyIds);
      return this.deliveries.filter(delivery => delivery.company && selected.has(delivery.company._id));
    }
  },
  watch: {
    // Si se borra el ultimo Envio de una empresa elegida, esa empresa deja de
    // ser opcion: se la saca del filtro para no dejar un chip huerfano que
    // filtra por algo que ya no se ve en la lista.
    companyOptions(options) {
      const available = new Set(options.map(option => option._id));
      const kept = this.selectedCompanyIds.filter(id => available.has(id));
      if (kept.length !== this.selectedCompanyIds.length) {
        this.selectedCompanyIds = kept;
      }
    }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    unselectCompany(companyId) {
      this.selectedCompanyIds = this.selectedCompanyIds.filter(id => id !== companyId);
    },
    async load() {
      this.errorMessage = null;
      this.loading = true;
      try {
        const [deliveries, purposes] = await Promise.all([
          emailDeliveryService.list(),
          emailTemplateService.purposes()
        ]);
        this.deliveries = deliveries;
        this.purposes = purposes;
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar los Envíos");
      } finally {
        this.loading = false;
      }
    },
    // El backend manda el motivo real. Se muestra tal cual y solo se cae al
    // texto propio si no vino ninguno.
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    companyName(item) {
      return (item.company && item.company.name) || "";
    },
    // plainText y no el nombre crudo: si el dato guardado trae marcado, la
    // columna mostraria "<p>Prueba</p>". Mismo criterio que el selector del
    // formulario.
    templateName(item) {
      return plainText((item.template && item.template.name) || "");
    },
    // El texto visible sale del lookup, no de una lista escrita a mano en la
    // pantalla: dos listas que hay que mantener en acuerdo terminan en
    // desacuerdo.
    purposeText(value) {
      const purpose = this.purposes.find(p => p.value === value);
      return (purpose && purpose.text) || value;
    },
    // Un Envio de etiquetas convive con los envios viejos por etiquetas de la
    // misma empresa (company.tagMailings), que el sistema sigue procesando
    // hoy (ver tagMailingsDueToday en scheduled-run-plan.js del backend). Un
    // envio viejo cuenta como "activo" con el mismo criterio que usa ese
    // filtro, salvo la coincidencia de dia: activo, no borrado y con al menos
    // una direccion de destinatario cargada. No se replica la coincidencia de
    // dia porque esta pantalla no es por dia -un envio viejo que hoy no cae
    // va a caer otro dia, y para entonces el duplicado ya existe si el Envio
    // nuevo tambien sigue activo-. Mirar solo "existe alguno" (sin isActive,
    // sin destinatarios) avisaria de mas y el aviso se volveria ruido.
    hasActiveOldTagMailing(company) {
      return (company.tagMailings || []).some(tagMailing =>
        tagMailing.isActive
        && !tagMailing.isDeleted
        && (tagMailing.recipientEmails || []).some(email => email && email.trim())
      );
    },
    // Un Envío diario o mensual convive con el interruptor viejo de la misma
    // empresa (sendDailyEmail / sendMonthlyEmail), y uno de etiquetas con los
    // envios viejos por etiquetas de company.tagMailings. Si el viejo sigue
    // activo para el mismo propósito, el destinatario recibe el correo por
    // los dos caminos: es la consecuencia que el usuario acepto
    // explicitamente, y esta pantalla es el unico lugar donde se puede ver
    // antes de que pase.
    hasDuplicateRisk(item) {
      const company = item.company;
      if (!company) { return false; }
      if (item.purpose === PURPOSE_DAILY) { return !!company.sendDailyEmail; }
      if (item.purpose === PURPOSE_MONTHLY) { return !!company.sendMonthlyEmail; }
      if (item.purpose === PURPOSE_TAG) { return this.hasActiveOldTagMailing(company); }
      return false;
    },
    duplicateRiskText(item) {
      if (item.purpose === PURPOSE_TAG) {
        return "Esta empresa también tiene activo un envío de etiquetas del camino anterior: los destinatarios van a recibir dos correos.";
      }
      const oldPathLabel = item.purpose === PURPOSE_DAILY ? "diario" : "mensual";
      return `Esta empresa también tiene activo el envío ${oldPathLabel} del camino anterior: el contratista va a recibir dos correos.`;
    },
    newDelivery() {
      this.deliveryForEdit = {
        company: "",
        purpose: "",
        template: "",
        isActive: true,
        monthly: { schedule: null },
        reminder: { days: [] },
        tag: { tags: [], schedule: null, recipientEmails: [] },
        monthEndReport: { schedule: null, recipientEmails: [], usePreviousMonth: false }
      };
      this.showDialog = true;
    },
    editDelivery(item) {
      this.deliveryForEdit = JSON.parse(JSON.stringify(item));
      this.showDialog = true;
    },
    cancelEdit() {
      this.showDialog = false;
      this.deliveryForEdit = null;
    },
    async onSaved() {
      this.cancelEdit();
      await this.load();
    },
    attachesSheet(item) {
      return item.purpose === "monthEndReport" && !!(item.monthEndReport && item.monthEndReport.attachPdf);
    },
    // Los datos de la fila elegida, para que se vea que Envio se esta probando.
    testSummary(item) {
      return [
        { label: "Empresa", value: this.companyName(item) },
        { label: "Propósito", value: this.purposeText(item.purpose) },
        { label: "Plantilla", value: this.templateName(item) },
        { label: "Asunto", value: item.subject },
        { label: "Activo", value: item.isActive ? "Sí" : "No" }
      ].filter(row => row.value);
    },
    testDelivery(item) {
      this.deliveryForTest = item;
    },
    async remove(item) {
      // Confirmacion antes de borrar: el dialogo global de la app (mixin
      // botConfirmDialog) resuelve con OK y rechaza con Cancelar.
      try {
        await this.botConfirmDialog({
          title: "Borrar Envío",
          content: `¿Borrar el Envío ${this.purposeText(item.purpose)} de ${this.companyName(item)}? ` +
            "Deja de salir desde ahora; los correos ya enviados no se tocan."
        });
      } catch (cancelled) {
        return;
      }
      this.errorMessage = null;
      this.saving = true;
      try {
        await emailDeliveryService.remove(item._id);
        await this.load();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo borrar el Envío");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
